import React, { useState, useRef, useEffect } from 'react';
import { Upload, X, Trash2, Clock, Monitor, Image as ImageIcon, PlayCircle, Eye, EyeOff } from 'lucide-react';
import { AdAsset, Hospital, ScreenSession, HospitalPreferences } from '../types';
import { apiFetch } from '../services/cloudflareService';

interface ScreenPlaylistManagerProps {
    screen: ScreenSession;
    hospital: Hospital;
    primaryColor: string;
    onUpdatePreferences: (updates: Partial<HospitalPreferences>) => Promise<boolean>;
}

export const ScreenPlaylistManager: React.FC<ScreenPlaylistManagerProps> = ({ screen, hospital, primaryColor, onUpdatePreferences }) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [uploading, setUploading] = useState(false);
    const [assets, setAssets] = useState<AdAsset[]>([]);

    useEffect(() => {
        // Filter assets that are targeted exclusively to this screen
        const allAssets = hospital.preferences?.adAssets || [];
        setAssets(allAssets.filter(a => a.targetScreenId === screen.id));
    }, [hospital, screen.id]);

    const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setUploading(true);
        try {
            const formData = new FormData();
            formData.append('file', file);
            formData.append('hospitalId', hospital.id);
            formData.append('adType', 'FULLSCREEN_SLIDE');

            const baseUrl = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' ? 'http://localhost:8787' : '';
            const res = await apiFetch(`${baseUrl}/api/ad-media/upload`, { method: 'POST', credentials: 'include', body: formData });
            
            if (!res.ok) {
                if (res.status === 401) {
                    alert('Tu sesión ha expirado. Por favor, inicia sesión nuevamente.');
                    window.location.href = '/login';
                    return;
                }
                const errData = await res.json().catch(() => null);
                alert(`❌ ${errData?.error || 'Error desconocido al subir archivo.'}`);
                return;
            }
            
            const data = await res.json();
            
            // Create the new AdAsset
            const newAsset: AdAsset = {
                id: `AD-${Date.now()}-${Math.random().toString(36).slice(2, 6).toUpperCase()}`,
                type: 'FULLSCREEN_SLIDE',
                target: 'TV_ONLY',
                targetScreenId: screen.id,
                imageUrl: data.imageUrl,
                imageAlt: file.name,
                mediaType: data.mediaType || 'image',
                displayTimeSec: 15,
                isActive: true,
                priority: hospital.preferences?.adAssets?.length ? hospital.preferences.adAssets.length + 1 : 1,
                createdAt: new Date().toISOString()
            };

            const allAssets = [...(hospital.preferences?.adAssets || []), newAsset];
            const success = await onUpdatePreferences({ adAssets: allAssets });
            if (success) {
                setAssets(prev => [...prev, newAsset]);
            } else {
                alert('Archivo subido, pero hubo un error al guardar las preferencias.');
            }
        } catch (err) {
            console.error('Error uploading file:', err);
            alert('❌ No se pudo conectar al servidor. Verifica tu conexión.');
        } finally {
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const handleDelete = async (assetId: string) => {
        if (!window.confirm('¿Seguro que deseas eliminar este contenido?')) return;
        
        const asset = assets.find(a => a.id === assetId);
        if (asset?.imageUrl && asset.imageUrl.startsWith('/api/ad-media/')) {
            try {
                const key = asset.imageUrl.replace('/api/ad-media/', '');
                const baseUrl = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1' ? 'http://localhost:8787' : '';
                await apiFetch(`${baseUrl}/api/ad-media`, {
                    method: 'DELETE',
                    headers: { 'Content-Type': 'application/json' },
                    credentials: 'include',
                    body: JSON.stringify({ key }),
                });
            } catch (e) {
                console.error('Error deleting media from storage', e);
            }
        }
        
        const allAssets = (hospital.preferences?.adAssets || []).filter(a => a.id !== assetId);
        const success = await onUpdatePreferences({ adAssets: allAssets });
        if (success) {
            setAssets(prev => prev.filter(a => a.id !== assetId));
        }
    };

    const toggleActive = async (assetId: string) => {
        const asset = assets.find(a => a.id === assetId);
        if (!asset) return;

        const allAssets = (hospital.preferences?.adAssets || []).map(a => 
            a.id === assetId ? { ...a, isActive: !a.isActive } : a
        );
        const success = await onUpdatePreferences({ adAssets: allAssets });
        if (success) {
            setAssets(prev => prev.map(a => a.id === assetId ? { ...a, isActive: !a.isActive } : a));
        }
    };

    return (
        <div className="flex flex-col h-full bg-slate-50 rounded-2xl border border-slate-200 overflow-hidden">
            <div className="p-6 border-b border-slate-200 bg-white">
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                            <Monitor className="w-5 h-5" style={{ color: primaryColor }} />
                            Contenido Exclusivo
                        </h3>
                        <p className="text-xs text-slate-500 mt-1">
                            Este contenido se mostrará <span className="font-bold">únicamente</span> en la pantalla "{screen.screenName}".
                        </p>
                    </div>
                    <button 
                        onClick={() => fileInputRef.current?.click()}
                        disabled={uploading}
                        className="px-4 py-2 text-white font-bold rounded-xl shadow-lg transition-all flex items-center gap-2 text-sm disabled:opacity-50"
                        style={{ backgroundColor: primaryColor }}
                    >
                        {uploading ? (
                            <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        ) : (
                            <Upload className="w-4 h-4" />
                        )}
                        Subir Archivo
                    </button>
                    <input 
                        type="file" 
                        ref={fileInputRef}
                        onChange={handleFileUpload}
                        className="hidden" 
                        accept="image/jpeg,image/png,image/gif,image/webp,video/mp4"
                    />
                </div>
            </div>

            <div className="flex-1 p-6 overflow-y-auto">
                {assets.length === 0 ? (
                    <div className="text-center py-12 border-2 border-dashed border-slate-200 rounded-2xl bg-white">
                        <div className="w-16 h-16 rounded-full bg-indigo-50 flex items-center justify-center mx-auto mb-4">
                            <ImageIcon className="w-8 h-8 text-indigo-300" />
                        </div>
                        <h4 className="text-sm font-bold text-slate-700 mb-1">Sin contenido exclusivo</h4>
                        <p className="text-xs text-slate-400 max-w-xs mx-auto">
                            Sube imágenes (JPG, PNG) o videos (MP4) para mostrarlos en el modo "Promos" de esta pantalla.
                        </p>
                    </div>
                ) : (
                    <div className="space-y-3">
                        {assets.map((asset) => (
                            <div key={asset.id} className={`bg-white rounded-xl border p-3 flex items-center gap-4 transition-all group shadow-sm ${asset.isActive ? 'border-slate-200' : 'border-slate-200 opacity-60'}`}>
                                <div className="w-16 h-16 rounded-lg bg-slate-100 overflow-hidden shrink-0 flex items-center justify-center border border-slate-200 relative">
                                    {asset.mediaType === 'video' ? (
                                        <>
                                            <video src={asset.imageUrl} className="w-full h-full object-cover" />
                                            <div className="absolute inset-0 bg-black/20 flex items-center justify-center">
                                                <PlayCircle className="w-6 h-6 text-white drop-shadow-md" />
                                            </div>
                                        </>
                                    ) : (
                                        <img src={asset.imageUrl} className="w-full h-full object-cover" alt="Ad preview" />
                                    )}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex gap-2 items-center mb-1">
                                        <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded" style={{ backgroundColor: `${primaryColor}15`, color: primaryColor }}>
                                            {asset.mediaType === 'video' ? 'Video' : 'Imagen'}
                                        </span>
                                        <span className="text-[10px] text-slate-400 flex items-center gap-1 font-medium">
                                            <Clock className="w-3 h-3" /> {asset.displayTimeSec}s
                                        </span>
                                    </div>
                                    <p className="text-xs text-slate-600 truncate font-medium">{asset.imageAlt || 'Contenido Exclusivo'}</p>
                                </div>
                                <div className="flex items-center gap-2 pr-2">
                                    <button 
                                        onClick={() => toggleActive(asset.id)} 
                                        className="p-2 transition-colors hover:bg-slate-50 rounded-lg text-slate-400 hover:text-slate-600" 
                                        title={asset.isActive ? "Desactivar" : "Activar"}
                                    >
                                        {asset.isActive ? <Eye className="w-4 h-4" style={{ color: primaryColor }} /> : <EyeOff className="w-4 h-4" />}
                                    </button>
                                    <button 
                                        onClick={() => handleDelete(asset.id)} 
                                        className="p-2 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors" 
                                        title="Eliminar"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};
